import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Code, Monitor, Play, Shield, Globe, Laptop } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { generateCourseSlug } from "@/utils/slugUtils";
import { useI18n } from "@/i18n/LanguageContext";
import { translateDbName } from "@/i18n/dbNames";


type Faculty = {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string;
  order_index: number;
  is_active: boolean;
};
type Course = {
  id: string;
  faculty_id: string;
  name: string;
  description: string | null;
  curriculum: string | null;
  image_url: string | null;
  individual_price: number | null;
  group_price: number | null;
  duration_months: number | null;
  weekly_frequency: number | null;
  format: string[] | null;
  learning_type: string[] | null;
  languages: string[] | null;
  what_awaits_you: string | null;
  instructors: string[] | null;
  admission_deadline: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  order_index: number;
  is_active: boolean;
  discount_percentage: number | null;
};
export const FacultyCourses = () => {
  const { t, locale } = useI18n();
  // Fetch faculties with their courses
  const {
    data: facultiesWithCourses = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['faculties-with-courses'],
    queryFn: async () => {
      console.log('Fetching faculties...');
      const {
        data: faculties,
        error: facultiesError
      } = await supabase.from('faculties').select('*').eq('is_active', true).order('order_index', {
        ascending: true
      });
      if (facultiesError) {
        console.error('Faculty error:', facultiesError);
        throw facultiesError;
      }
      console.log('Found faculties:', faculties?.length || 0);

      // Fetch courses for each faculty
      const facultiesWithCourses = await Promise.all((faculties as Faculty[]).map(async faculty => {
        console.log(`Fetching courses for faculty: ${faculty.name}`);
        const {
          data: courses,
          error: coursesError
        } = await supabase.from('courses').select('*').eq('faculty_id', faculty.id).eq('is_active', true)
        .order('order_index', {
          ascending: true
        });
        if (coursesError) {
          console.error(`Course error for ${faculty.name}:`, coursesError);
          throw coursesError;
        }
        console.log(`Found ${courses?.length || 0} courses for ${faculty.name}`);
        return {
          ...faculty,
          courses: courses as Course[]
        };
      }));
      console.log('Final data:', facultiesWithCourses);
      return facultiesWithCourses;
    }
  });
  if (error) {
    console.error('Query error:', error);
    return <div className="container mx-auto px-4 py-16">
        <div className="text-center text-destructive">
          <p>{t.faculties.error}</p>
        </div>
      </div>;
  }
  const getIcon = (iconString: string | null) => {
    console.log('Icon string:', iconString);
    switch (iconString) {
      case 'Monitor':
        return <Monitor className="w-8 h-8" />;
      case 'Code':
        return <Code className="w-8 h-8" />;
      case 'Play':
        return <Play className="w-8 h-8" />;
      case 'Shield':
        return <Shield className="w-8 h-8" />;
      case 'Globe':
        return <Globe className="w-8 h-8" />;
      case 'Laptop':
        return <Laptop className="w-8 h-8" />;
      default:
        return <Code className="w-8 h-8" />;
    }
  };
  const hexToHsl = (hex: string): string => {
    const r = parseInt(hex.substr(1, 2), 16) / 255;
    const g = parseInt(hex.substr(3, 2), 16) / 255;
    const b = parseInt(hex.substr(5, 2), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };
  const getGradientFromColor = (color: string) => {
    // Convert hex color to gradient classes - case insensitive
    const upperColor = color.toUpperCase();
    const colorMapping: {
      [key: string]: string;
    } = {
      '#8E44AD': 'from-purple-600 to-purple-700',
      '#6C3483': 'from-purple-700 to-purple-800',
      '#2C3E50': 'from-slate-700 to-slate-800',
      '#8800C2': 'from-purple-600 to-purple-700',
      '#E74C3C': 'from-red-500 to-red-600',
      '#3498DB': 'from-blue-500 to-blue-600',
      '#2ECC71': 'from-green-500 to-green-600',
      '#F39C12': 'from-yellow-500 to-yellow-600',
      '#9B59B6': 'from-purple-500 to-purple-600',
      '#1ABC9C': 'from-teal-500 to-teal-600',
      '#34495E': 'from-slate-500 to-slate-600',
      '#E67E22': 'from-orange-500 to-orange-600'
    };
    if (colorMapping[upperColor]) {
      return colorMapping[upperColor];
    }

    // Fallback: create dynamic gradient using HSL
    const hsl = hexToHsl(color);
    const lightness = parseInt(hsl.split(' ')[2].replace('%', ''));
    const darkerLightness = Math.max(0, lightness - 10);
    const darkerHsl = hsl.replace(/(\d+)%\)$/, `${darkerLightness}%)`);
    return `from-[hsl(${hsl})] to-[hsl(${darkerHsl})]`;
  };
  const getBorderFromColor = (color: string) => {
    // Case insensitive color mapping
    const upperColor = color.toUpperCase();
    const borderMapping: {
      [key: string]: string;
    } = {
      '#8E44AD': 'border-purple-500/30',
      '#6C3483': 'border-purple-600/30',
      '#2C3E50': 'border-slate-600/30',
      '#8800C2': 'border-purple-600/30',
      '#E74C3C': 'border-red-500/30',
      '#3498DB': 'border-blue-500/30',
      '#2ECC71': 'border-green-500/30',
      '#F39C12': 'border-yellow-500/30',
      '#9B59B6': 'border-purple-500/30',
      '#1ABC9C': 'border-teal-500/30',
      '#34495E': 'border-slate-500/30',
      '#E67E22': 'border-orange-500/30'
    };
    if (borderMapping[upperColor]) {
      return borderMapping[upperColor];
    }

    // Fallback: create dynamic border using HSL
    const hsl = hexToHsl(color);
    return `border-[hsl(${hsl}/0.3)]`;
  };
  const getHoverEffect = (status: string) => {
    switch (status) {
      case 'new':
        return 'hover:bg-blue-500/20';
      case 'enrollment':
        return 'hover:bg-yellow-500/20';
      case 'discount':
        return 'hover:bg-orange-500/20';
      case 'action':
        return 'hover:bg-red-500/20';
      case 'active':
        return 'hover:bg-green-500/20';
      case 'draft':
        return 'hover:bg-gray-500/20';
      case 'full':
        return 'hover:bg-purple-500/20';
      case 'completed':
        return 'hover:bg-slate-500/20';
      default:
        return 'hover:bg-gray-500/20';
    }
  };
  const getTechDisplay = (courseName: string) => {
    // Extract technology from course name
    if (courseName.includes('React')) return 'React';
    if (courseName.includes('Node')) return 'Node';
    if (courseName.includes('JavaScript') || courseName.includes('Javascript')) return 'JS';
    if (courseName.includes('PHP')) return 'PHP';
    if (courseName.includes('Python')) return 'Python';
    if (courseName.includes('C++')) return 'C++';
    if (courseName.includes('C#')) return 'C#';
    if (courseName.includes('Java')) return 'Java';
    if (courseName.includes('Unity') || courseName.includes('Խաղ')) return 'Unity';
    if (courseName.includes('AI')) return 'AI';
    if (courseName.includes('Photoshop')) return 'Ps';
    if (courseName.includes('Illustrator')) return 'Ai';
    if (courseName.includes('After Effects')) return 'Ae';
    if (courseName.includes('Premiere')) return 'Pr';
    if (courseName.includes('UI') || courseName.includes('UX')) return 'UI';
    if (courseName.includes('դիզայն')) return 'Design';
    if (courseName.includes('Կիբեռ')) return 'Cyber';
    return 'Code';
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "enrollment":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "discount":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400";
      case "action":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "draft":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      case "full":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400";
      case "completed":
        return "bg-slate-100 text-slate-800 dark:bg-slate-900/20 dark:text-slate-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };
  const getStatusDisplay = (status: string, discountPercentage?: number) => {
    switch (status) {
      case "new":
        return t.faculties.status.new;
      case "discount":
        return discountPercentage ? `${t.faculties.status.discount} ${discountPercentage}%` : t.faculties.status.discount;
      case "action":
        return t.faculties.status.action;
      case "enrollment":
        return t.faculties.status.enrollment;
      case "active":
        return t.faculties.status.active;
      case "draft":
        return t.faculties.status.draft;
      case "full":
        return t.faculties.status.full;
      case "completed":
        return t.faculties.status.completed;
      case "none":
      case "":
      case null:
      case undefined:
      default:
        return ""; // Don't show anything if status is none, empty, or not specified
    }
  };
  if (isLoading) {
    return <section className="app-section bg-background" aria-busy="true">
        <div className="app-container">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="section-title">{t.faculties.title}</h2>
            <p className="section-copy mx-auto">{t.faculties.loading}</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-40 animate-pulse rounded-lg bg-muted" />)}
          </div>
        </div>
      </section>;
  }

  // Don't render anything if no faculties are available
  if (facultiesWithCourses.length === 0) {
    return null;
  }
  return <section id="faculties" className="app-section scroll-mt-20 bg-background">
      <div className="app-container">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="section-title">{t.faculties.title}</h2>
          <p className="section-copy mx-auto">
            {t.faculties.copy}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-8">
          {facultiesWithCourses.map((faculty, index) => <div key={faculty.id} className="space-y-6">
              {/* Faculty Header */}
              <div className={`bg-gradient-to-r ${getGradientFromColor(faculty.color)} p-6 rounded-t-lg border-2 ${getBorderFromColor(faculty.color)}`}>
                <div className="flex items-center justify-center space-x-3 text-white">
                  {getIcon(faculty.icon)}
                  <h3 className="text-xl font-bold text-center">{translateDbName(faculty.name, locale)}</h3>
                </div>
              </div>

              {/* Courses */}
              <div className="space-y-4">
                {faculty.courses.length > 0 ? faculty.courses.map(course => <Link key={course.id} to={`/course/${generateCourseSlug(course.name)}`} className="block">
                      <Card className={`app-card ${getBorderFromColor(faculty.color)} cursor-pointer group relative overflow-hidden`}>
                        <CardContent className="p-4 h-full relative">
                          {/* Default Content - Course Name and Status */}
                          <div className="flex justify-between items-center h-full group-hover:opacity-0 transition-opacity duration-300">
                            <h4 className="text-sm font-semibold text-foreground group-hover:text-highlight">
                              {translateDbName(course.name, locale)}
                            </h4>
                            {(() => {
                              const statusDisplay = getStatusDisplay(course.status, course.discount_percentage || undefined);
                              return statusDisplay && statusDisplay.trim() !== "" && (
                                <Badge className={`text-xs ${getStatusColor(course.status)}`}>
                                  {statusDisplay}
                                </Badge>
                              );
                            })()}
                          </div>
                          
                          {/* Hover Content - Course Name only */}
                          <div className="flex items-center justify-between h-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 absolute inset-0 p-4">
                             <h4 className="text-sm font-semibold text-highlight">
                              {translateDbName(course.name, locale)}
                            </h4>
                          </div>
                          
                          {/* Hover Content - Image on the right (24% width) */}
                          <div className="absolute inset-y-0 right-0 w-[24%] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                             {course.image_url ? (
                              <img
                                src={course.image_url}
                                alt={translateDbName(course.name, locale)}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : <div className="w-full h-full bg-gradient-to-r from-primary to-highlight flex items-center justify-center text-primary-foreground font-bold text-xl">
                                {getTechDisplay(course.name)}
                              </div>}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>) : <div className="text-center text-muted-foreground py-8">
                    {t.faculties.soon}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};